import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';
import axiosInstance from '../axiosConfig';
import { useParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import '../styles.css';

const EmployeeCalendar = () => {
  const { employeeId } = useParams();
  const currentUserId = localStorage.getItem('id');
  const [currentUser, setCurrentUser] = useState({});
  const [data, setData] = useState({});
  const [events, setEvents] = useState([]);
  const [attendanceDetails, setAttendanceDetails] = useState({});
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(currentUserId);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [allAttendanceData, setAllAttendanceData] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState({});

  useEffect(() => {
    const screenWidth = window.innerWidth;
    setIsSidebarVisible(screenWidth >= 520);
  }, []);

  const handleToggleClick = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  useEffect(() => {
    axiosInstance.get('/user/')
      .then(response => {
        const usersData = response.data.map(user => ({
          id: user.id,
          name: `${user.first_name} ${user.last_name}`,
          role: user.role,
          employeeCode: user.employee_code
        }));
        setUsers(usersData);

        axiosInstance.get(`/user/${currentUserId}/`)
          .then(response => {
            const userData = response.data;
            if (userData.role) {
              setCurrentUser({ ...userData, role: userData.role.name });
            } else {
              const fallbackRole = userData.role[0]?.name || 'guest';
              setCurrentUser({ ...userData, role: fallbackRole });
            }
          })
          .catch(error => {
            console.error(error);
          });
      })
      .catch(error => {
        console.error(error);
      });
  }, []);

  useEffect(() => {
    fetchAllAttendanceData();
  }, [selectedUser, employeeId]);

  const fetchAllAttendanceData = async () => {
    try {
      const response = await axiosInstance.get(`/attendance/user/${employeeId}/`);
      setAllAttendanceData(response.data);
      const formattedEvents = formatAttendanceData(response.data);
      setEvents(formattedEvents);
      if (response.data.length > 0) {
        const attendanceDetails = response.data[0];
        setAttendanceDetails({
          userName: attendanceDetails.user_name,
          employeeCode: attendanceDetails.employee_code,
        });
      }
    } catch (error) {
      console.error('Error fetching attendance data:', error);
    }
  };

  const formatAttendanceData = (attendance) => {
    return attendance.map((item) => {
      let status = item.status;
      let color;
      let title = status.charAt(0).toUpperCase() + status.slice(1);

      switch (status) {
        case 'present':
          color = 'green';
          break;
        case 'half_day':
          color = 'grey';
          break;
        case 'absent':
          color = 'red';
          break;
        case 'leave':
          color = 'purple';
          break;
        case 'missed_punch':
          color = 'blue';
          break;
        case 'holiday':
        case 'weekly_off':
          color = 'orange';
          title = 'WO(week off)';
          break;
        default:
          color = 'grey';
      }

      return {
        title,
        date: item.date,
        backgroundColor: color,
        borderColor: color,
        punchInTime: item.punch_in_time,
        punchOutTime: item.punch_out_time,
      };
    });
  };

  // Generate WO entries for every Sunday (every month/year visible)
  const getWoEvents = () => {
    const wo = [];
    // Cover a wide range so that switching months/years still shows WO.
    for (let year = 2022; year <= 2030; year++) {
      for (let month = 0; month < 12; month++) {
        const d = new Date(year, month, 1);
        while (d.getMonth() === month) {
          // Sunday => 0 in JS Date
          if (d.getDay() === 1) {
            const iso = d.toISOString().slice(0, 10);
            wo.push({
              title: 'WO(week off)',
              date: iso,
              backgroundColor: 'orange',
              borderColor: 'orange',
            });
          }
          d.setDate(d.getDate() + 1);
        }
      }
    }
    return wo;
  };


  const handleDateClick = (arg) => {
    const attendanceDataForDate = allAttendanceData.find((data) => data.date === arg.dateStr);
    if (attendanceDataForDate) {
      setPopupData({
        punchInTime: attendanceDataForDate.punch_in_time,
        punchOutTime: attendanceDataForDate.punch_out_time,
      });
      setShowPopup(true);
    }
  };

  const handleMouseOut = () => {
    setShowPopup(false);
  };

  const leaveDays = data.leave_requests && data.leave_requests.length > 0
    ? data.leave_requests.map((leaveRequest) => ({
        title: leaveRequest.leave_type,
        start: leaveRequest.start_date,
        end: leaveRequest.end_date,
        color: 'purple',
      }))
    : [];

  const handleUserChange = (userId) => {
    setSelectedUser(userId);
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  const menuItems = [
    { icon: '&#127968;', label: 'Dashboard', path: '/admin-manager-lead-dashboard' },
    { icon: '&#128196;', label: 'AR Requests', path: '/all-requests' },
    // { icon: '&#128197;', label: 'Calendar', path: '/calendar' },
    ...(currentUser?.role_name === 'admin' || currentUser?.role_name === 'manager' ? [
      { icon: '&#128101;', label: 'Employees', path: '/employees' },
      { icon: '&#127970;', label: 'Roles & Office', path: '/roles-office' },
    ] : []),
  ];

  return (
    <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Header
        onToggleClick={handleToggleClick}
        isSidebarVisible={isSidebarVisible}
      />
      
      <Sidebar
        isSidebarVisible={isSidebarVisible}
        currentUser={currentUser}
        menuItems={menuItems}
        handleLogout={handleLogout}
      />
      
      <main className="dashboard-main-content">
        <div className="dashboard-header-section">
          <h1>📅 Employee Calendar</h1>
          <p>{attendanceDetails?.userName} ({attendanceDetails?.employeeCode})</p>
        </div>

        <div className="dashboard-card full-width">
          <div className="calendar-wrapper">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              events={[...getWoEvents(), ...events, ...leaveDays]}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
              }}
              footerToolbar={{
                left: '',
                center: '',
                right: '',
              }}
              dayRender={(date, cell) => {
                if (date.getDay() === 0 || date.getDay() === 6) {
                  cell.css("background-color", "#ccc");
                  cell.css("color", "#333");
                }
              }}
              dateClick={handleDateClick}
              mouseOut={handleMouseOut}
              height="auto"
            />
          </div>
        </div>

        {showPopup && (
          <div className="employee-calendar-popup-overlay" onClick={() => setShowPopup(false)}>
            <div className="employee-calendar-popup" onClick={(e) => e.stopPropagation()}>
              <div className="employee-calendar-popup-header">
                <h3>Attendance Details</h3>
                <button className="employee-calendar-popup-close" onClick={() => setShowPopup(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="employee-calendar-popup-body">
                <div className="employee-calendar-popup-row">
                  <span className="popup-label">Punch-in:</span>
                  <span className="popup-value">{popupData.punchInTime || 'N/A'}</span>
                </div>
                <div className="employee-calendar-popup-row">
                  <span className="popup-label">Punch-out:</span>
                  <span className="popup-value">{popupData.punchOutTime || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* <div className="dashboard-card full-width">
          <div className="color-legend">
            <div className="legend-item">
              <span className="legend-dot present"></span> Present
            </div>
            <div className="legend-item">
              <span className="legend-dot absent"></span> Absent
            </div>
            <div className="legend-item">
              <span className="legend-dot leave"></span> Leave
            </div>
            <div className="legend-item">
              <span className="legend-dot half-day"></span> Half Day
            </div>
            <div className="legend-item">
              <span className="legend-dot missed"></span> Missed Punch
            </div>
            <div className="legend-item">
              <span className="legend-dot holiday"></span> Holiday / Weekly Off
            </div>
          </div>
        </div> */}
      </main>
    </div>
  );
};

export default EmployeeCalendar;

